import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '../../../../shared/i18n';
import { monitoredActivitiesKidApi } from '../../api/monitoredActivities';

const CLIP_DURATION_MS = 3000;
const CLIP_FPS = 15;
const BEEP_INTERVAL_MS = 10000;
const TICK_MS = 250;

// Short "operation confirmation" beep via WebAudio (no assets needed).
// 2.5 kHz sits in the classic device-notification sweet spot (2–4 kHz),
// which the ear is most sensitive to and reads as a crisp confirmation
// tone rather than a warning. Safe to call from any handler.
const playBeep = (audioCtxRef) => {
  try {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtxRef.current = new Ctx();
    }
    const ctx = audioCtxRef.current;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 2500;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.2, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.14);
  } catch (e) {
    // ignore audio errors
  }
};

const formatTime = (seconds) => {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, '0')}`;
};

const PauseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 320 512" aria-hidden="true">
    <path d="M48 64C21.5 64 0 85.5 0 112V400c0 26.5 21.5 48 48 48H80c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48H48zm192 0c-26.5 0-48 21.5-48 48V400c0 26.5 21.5 48 48 48h32c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48H240z"/>
  </svg>
);

const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 384 512" aria-hidden="true">
    <path d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"/>
  </svg>
);

const CancelIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 384 512" aria-hidden="true">
    <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/>
  </svg>
);

export const MonitoredActivitySessionPage = () => {
  const { sessionId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [status, setStatus] = useState('pending'); // pending | paused | completed | cancelled
  const debug = session?.debug === true;
  const [elapsed, setElapsed] = useState(0); // seconds of ACTIVE (unpaused) time
  const [uploaded, setUploaded] = useState(new Set());
  const [recordingIndex, setRecordingIndex] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const activeStartRef = useRef(null); // timestamp when active phase started
  const pausedElapsedRef = useRef(0);   // elapsed carried over across pauses
  const tickRef = useRef(null);
  const beepTimerRef = useRef(null);
  const captureFiredRef = useRef(new Set());

  const totalLengthSeconds = session?.monitored_activity?.length_seconds || 0;
  const schedule = session?.planned_clip_schedule || [];

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t('children.monitored_session.camera_not_supported'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { frameRate: { ideal: CLIP_FPS, max: CLIP_FPS } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (e) {
      setCameraError(e.message || 'Camera access denied');
    }
  }, [t]);

  // Load session
  useEffect(() => {
    monitoredActivitiesKidApi
      .showSession(sessionId)
      .then((res) => {
        setSession(res.data.monitored_activity_session);
        setStatus(res.data.monitored_activity_session.status);
      })
      .catch((e) => setLoadError(e.message));
  }, [sessionId]);

  // Start camera once session is loaded (and not already terminal)
  useEffect(() => {
    if (session && ['pending', 'paused'].includes(status)) {
      startCamera();
    }
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  const captureClip = useCallback(
    async (clipIndex) => {
      if (!streamRef.current || captureFiredRef.current.has(clipIndex)) return;
      captureFiredRef.current.add(clipIndex);
      setRecordingIndex(clipIndex);

      const chunks = [];
      let mimeType = 'video/webm;codecs=vp9';
      if (typeof MediaRecorder !== 'undefined' && !MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm';
      }
      let recorder;
      try {
        recorder = new MediaRecorder(streamRef.current, { mimeType, videoBitsPerSecond: 500_000 });
      } catch (e) {
        setRecordingIndex(null);
        return;
      }
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: mimeType });
        try {
          await monitoredActivitiesKidApi.uploadClip(sessionId, clipIndex, blob);
          setUploaded((prev) => {
            const next = new Set(prev);
            next.add(clipIndex);
            return next;
          });
        } catch (e) {
          // Silent — kid can still finish session; clip capture is best-effort.
        } finally {
          setRecordingIndex(null);
        }
      };
      recorder.start();
      setTimeout(() => {
        if (recorder.state !== 'inactive') recorder.stop();
      }, CLIP_DURATION_MS);
    },
    [sessionId]
  );

  const tick = useCallback(() => {
    if (status !== 'pending') return;
    const now = Date.now();
    const activeSec = pausedElapsedRef.current + (activeStartRef.current ? (now - activeStartRef.current) / 1000 : 0);
    setElapsed(activeSec);

    // Fire scheduled clips
    schedule.forEach((offset, idx) => {
      if (activeSec >= offset && !captureFiredRef.current.has(idx)) {
        captureClip(idx);
      }
    });

    // Auto-complete when time elapsed
    if (totalLengthSeconds > 0 && activeSec >= totalLengthSeconds) {
      handleComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, schedule, totalLengthSeconds, captureClip]);

  const startBeep = useCallback(() => {
    stopBeep();
    // Kick off first beep on start
    playBeep(audioCtxRef);
    beepTimerRef.current = setInterval(() => {
      if (status === 'pending') playBeep(audioCtxRef);
    }, BEEP_INTERVAL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const stopBeep = () => {
    if (beepTimerRef.current) {
      clearInterval(beepTimerRef.current);
      beepTimerRef.current = null;
    }
  };

  // Manage timing loop
  useEffect(() => {
    if (status === 'pending') {
      if (activeStartRef.current == null) activeStartRef.current = Date.now();
      tickRef.current = setInterval(tick, TICK_MS);
      startBeep();
    }
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      stopBeep();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, tick]);

  const handlePause = async () => {
    if (status !== 'pending') return;
    // Freeze active time
    pausedElapsedRef.current = pausedElapsedRef.current + (activeStartRef.current ? (Date.now() - activeStartRef.current) / 1000 : 0);
    activeStartRef.current = null;
    try {
      await monitoredActivitiesKidApi.pause(sessionId);
    } catch (e) { /* ignore */ }
    setStatus('paused');
  };

  const handleResume = async () => {
    if (status !== 'paused') return;
    activeStartRef.current = Date.now();
    try {
      await monitoredActivitiesKidApi.resume(sessionId);
    } catch (e) { /* ignore */ }
    setStatus('pending');
  };

  const handleCancel = async () => {
    if (!window.confirm(t('children.monitored_session.confirm_cancel'))) return;
    try {
      await monitoredActivitiesKidApi.cancel(sessionId);
    } catch (e) { /* ignore */ }
    stopStream();
    setStatus('cancelled');
    setTimeout(() => navigate('/monitored-activities'), 800);
  };

  const handleComplete = async () => {
    if (['completed', 'cancelled'].includes(status)) return;
    try {
      await monitoredActivitiesKidApi.complete(sessionId);
    } catch (e) { /* ignore */ }
    stopStream();
    setStatus('completed');
    setTimeout(() => navigate('/monitored-activities'), 1500);
  };

  if (loadError) return <div className="alert alert-danger m-4">{loadError}</div>;
  if (!session) return <div className="text-center py-4"><div className="spinner-border" role="status" /></div>;

  const remainingSec = Math.max(0, totalLengthSeconds - elapsed);
  // Bar shows time REMAINING so kids can gauge from across the room:
  // green fills the bar while time is left, drains toward empty as it ends.
  const remainingPct = totalLengthSeconds > 0
    ? Math.max(0, Math.min(100, (remainingSec / totalLengthSeconds) * 100))
    : 0;

  // Colour shifts from healthy green → warm amber → alert red as time drains,
  // so the kid can feel urgency at a glance without needing to read the timer.
  const barGradient = remainingPct > 50
    ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
    : remainingPct > 20
      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';

  const isActive = status === 'pending' || status === 'paused';

  return (
    <div
      className="container-fluid px-3 py-3"
      style={{ maxWidth: 720, paddingBottom: isActive ? '96px' : '24px' }}
      data-testid="monitored-session-page"
      data-status={status}
    >
      <h2
        className="fw-bold mb-3 text-center"
        style={{ fontSize: 'clamp(1.25rem, 5vw, 1.75rem)', lineHeight: 1.2 }}
      >
        {session.monitored_activity?.name}
      </h2>

      {cameraError && (
        <div className="alert alert-warning rounded-4" data-testid="camera-error">{cameraError}</div>
      )}

      <div
        className="mb-3 rounded-4 overflow-hidden shadow-sm mx-auto position-relative"
        style={{ background: '#000', aspectRatio: '4 / 3', maxWidth: 560 }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          data-testid="monitored-session-video"
        />
        {recordingIndex != null && (
          <div
            className="position-absolute d-flex align-items-center gap-2 rounded-pill px-3 py-1 fw-bold text-white"
            style={{
              top: 12,
              left: 12,
              background: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              fontSize: 13,
              letterSpacing: '0.05em',
              pointerEvents: 'none',
            }}
            data-testid="recording-indicator"
          >
            <span
              aria-hidden="true"
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#ef4444',
                boxShadow: '0 0 6px rgba(239, 68, 68, 0.9)',
                animation: 'monitored-rec-blink 1s ease-in-out infinite',
              }}
            />
            REC
          </div>
        )}
        <style>{`
          @keyframes monitored-rec-blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.15; }
          }
        `}</style>
      </div>

      <div
        className="mx-auto mb-3"
        style={{ maxWidth: 560 }}
        data-testid="monitored-session-progress"
      >
        <div
          className="rounded-pill overflow-hidden"
          style={{
            height: 24,
            background: '#e5e7eb',
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.08)'
          }}
        >
          <div
            role="progressbar"
            style={{
              width: `${remainingPct}%`,
              height: '100%',
              background: barGradient,
              transition: 'width 250ms linear, background 400ms ease',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
            aria-valuenow={remainingPct}
            aria-valuemin="0"
            aria-valuemax="100"
            data-testid="monitored-session-progressbar"
          />
        </div>
        <div
          className="text-center mt-2 fw-bold text-secondary"
          style={{ fontSize: 'clamp(1.5rem, 6vw, 2rem)', fontVariantNumeric: 'tabular-nums' }}
          data-testid="monitored-session-timer"
        >
          <span style={{ color: '#111827' }}>{formatTime(remainingSec)}</span>
          <span className="mx-2 text-muted" style={{ fontWeight: 400 }}>/</span>
          <span className="text-muted" style={{ fontWeight: 400 }}>{formatTime(totalLengthSeconds)}</span>
        </div>
      </div>

      {debug && (
        <div className="mb-3 text-center" data-testid="monitored-session-debug">
          {t('children.monitored_session.clips_uploaded', { count: uploaded.size, total: schedule.length })}
        </div>
      )}

      {isActive && (
        <div
          className="d-flex gap-2 mx-auto"
          style={{ maxWidth: 560 }}
        >
          {status === 'pending' && (
            <button
              type="button"
              className="btn btn-warning fw-semibold rounded-4 d-inline-flex align-items-center justify-content-center gap-2 flex-fill"
              style={{ minHeight: 56, fontSize: 18 }}
              onClick={handlePause}
              data-testid="monitored-session-pause"
            >
              <PauseIcon />
              {t('children.monitored_session.pause')}
            </button>
          )}
          {status === 'paused' && (
            <button
              type="button"
              className="btn btn-primary fw-semibold rounded-4 d-inline-flex align-items-center justify-content-center gap-2 flex-fill"
              style={{
                minHeight: 56,
                fontSize: 18,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none'
              }}
              onClick={handleResume}
              data-testid="monitored-session-resume"
            >
              <PlayIcon />
              {t('children.monitored_session.resume')}
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-danger fw-semibold rounded-4 d-inline-flex align-items-center justify-content-center gap-2 flex-fill"
            style={{ minHeight: 56, fontSize: 18 }}
            onClick={handleCancel}
            data-testid="monitored-session-cancel"
          >
            <CancelIcon />
            {t('children.monitored_session.cancel')}
          </button>
        </div>
      )}

      {status === 'completed' && (
        <div className="alert alert-success rounded-4 text-center fs-5" data-testid="monitored-session-completed">
          {t('children.monitored_session.completed')}
        </div>
      )}

      {status === 'cancelled' && (
        <div className="alert alert-secondary rounded-4 text-center fs-5" data-testid="monitored-session-cancelled">
          {t('children.monitored_session.cancelled')}
        </div>
      )}
    </div>
  );
};

export default MonitoredActivitySessionPage;
